import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
  }

  static fromStatus(status: number, body: unknown): ApiError {
    const apiBody = body as Partial<ApiErrorBody> & { detail?: ApiErrorBody | string };
    const detail = apiBody?.detail;
    const errBody =
      detail && typeof detail === "object" && "error" in detail ? detail.error : apiBody?.error;
    const code = errBody?.code ?? (status === 401 ? "unauthorized" : "unknown");
    const message = errBody?.message ?? `HTTP ${status}`;
    return new ApiError(status, code, body, message);
  }
}

interface FetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { method = "GET", body, signal } = options;
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  };
  const resp = await fetch(path, init);
  if (resp.status === 204) {
    return undefined as T;
  }
  let parsed: unknown = null;
  const text = await resp.text();
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!resp.ok) {
    throw ApiError.fromStatus(resp.status, parsed);
  }
  return parsed as T;
}
